import React from 'react';
import { ArrowPathIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';

export interface FooterMenuAction {
    label: string;
    onClick: () => void;
    tone: 'amber' | 'red';
    /** Omit the item from the menu entirely (e.g. status-gated actions). */
    hidden?: boolean;
}

interface ActionFooterWithMenuProps {
    /** Primary CTA label shown while enabled. */
    primaryLabel: string;
    /** Primary CTA label shown while `canSubmit` is false and not submitting. */
    primaryDisabledLabel?: string;
    canSubmit: boolean;
    isSubmitting: boolean;
    onSubmit: () => void;
    menuActions: FooterMenuAction[];
    /** Overflow popover width. Wider menus (multi-line labels) can override. */
    menuWidthClass?: string;
}

const TONE_STYLES: Record<FooterMenuAction['tone'], string> = {
    amber: 'border-yellow-400 bg-yellow-50 text-[#B88716] hover:bg-yellow-100',
    red: 'border-red-300 bg-red-50 text-red-600 hover:bg-red-100'
};

// Pinned action-page footer: full-width primary CTA + kebab overflow menu, shared across the approve-style pages.
export function ActionFooterWithMenu({
    primaryLabel,
    primaryDisabledLabel,
    canSubmit,
    isSubmitting,
    onSubmit,
    menuActions,
    menuWidthClass = 'w-64'
}: ActionFooterWithMenuProps) {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const visibleActions = menuActions.filter((a) => !a.hidden);

    React.useEffect(() => {
        if (!menuOpen) return;

        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };

        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [menuOpen]);

    const handleMenuAction = (action: FooterMenuAction) => {
        setMenuOpen(false);
        action.onClick();
    };

    return (
        <div className="flex-shrink-0 mt-4 pt-4 pb-1 border-t border-gray-200">
            <div className="flex gap-2 items-center">
                <button
                    type="button"
                    onClick={canSubmit ? onSubmit : undefined}
                    disabled={!canSubmit || isSubmitting}
                    className={`flex-1 h-12 rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                        canSubmit && !isSubmitting
                            ? 'bg-gray-900 text-white hover:bg-gray-800 cursor-pointer'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                    {isSubmitting && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
                    {canSubmit ? primaryLabel : (primaryDisabledLabel ?? primaryLabel)}
                </button>

                {visibleActions.length > 0 && (
                    <div ref={menuRef} className="relative">
                        <button
                            type="button"
                            onClick={() => setMenuOpen((o) => !o)}
                            disabled={isSubmitting}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            aria-label="Other actions"
                            className="h-12 w-12 rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                        >
                            <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>

                        {menuOpen && (
                            <div
                                role="menu"
                                className={`absolute bottom-full mb-2 right-0 z-10 ${menuWidthClass} flex flex-col gap-1.5 p-1.5 bg-white border border-gray-200 rounded-xl shadow-lg`}
                            >
                                {visibleActions.map((action) => (
                                    <button
                                        key={action.label}
                                        type="button"
                                        role="menuitem"
                                        onClick={() => handleMenuAction(action)}
                                        disabled={isSubmitting}
                                        className={`h-10 rounded-lg border-[1.5px] text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${TONE_STYLES[action.tone]}`}
                                    >
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
